// OTP Verification Modal Component
import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, ArrowLeft, RefreshCw, Clock } from 'lucide-react';

const OTPVerificationModal = ({ 
  method, 
  sentTo, 
  expiresAt, 
  onVerify, 
  onResend, 
  onBack,
  loading,
  error 
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  // Countdown timer
  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setCanResend(true);
    }
  }, [timeRemaining]);

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newOtp = [...otp];
    newOtp[index] = value.replace(/\D/g, ''); // Only numbers
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    pastedData.split('').forEach((digit, i) => {
      if (i < 6) newOtp[i] = digit;
    });
    setOtp(newOtp);
    
    // Focus last filled input or first empty
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  // Handle verify
  const handleVerify = () => {
    const otpCode = otp.join('');
    if (otpCode.length === 6) {
      onVerify(otpCode);
    }
  };

  // Handle resend
  const handleResend = () => {
    setOtp(['', '', '', '', '', '']);
    setTimeRemaining(300);
    setCanResend(false);
    onResend();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center mx-auto mb-4">
          {method === 'SMS' ? (
            <Phone className="w-8 h-8 text-accent" />
          ) : (
            <Mail className="w-8 h-8 text-accent" />
          )}
        </div>
        <h3 className="font-display text-xl text-ink mb-2">
          Enter Verification Code
        </h3>
        <p className="text-ink-soft">
          We sent a 6-digit code to{' '}
          <span className="font-semibold text-ink">{sentTo}</span>
        </p>
      </div>

      {/* OTP Input */}
      <div className="space-y-4">
        <div className="flex justify-center gap-2">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className="w-12 h-14 text-center text-2xl font-semibold border-2 border-ink-faint rounded-sm bg-surface focus:border-accent focus:ring-1 focus:ring-accent transition-colors"
              disabled={loading}
            />
          ))}
        </div>

        {/* Timer */}
        <div className="flex items-center justify-center gap-2 text-sm text-ink-soft">
          <Clock className="w-4 h-4" />
          <span>
            {timeRemaining > 0
              ? `Code expires in ${formatTime(timeRemaining)}`
              : 'Code expired'
            }
          </span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-accent-soft border border-accent/30 text-accent-deep px-4 py-3 rounded-sm text-sm">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={handleVerify}
          disabled={loading || otp.join('').length !== 6 || timeRemaining === 0}
          className="w-full bg-accent text-paper py-3 px-4 rounded-full hover:bg-accent-deep focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dpc-label"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-paper mr-2"></div>
              Verifying...
            </div>
          ) : (
            'Verify Code'
          )}
        </button>

        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={onBack}
            className="text-ink-soft hover:text-ink flex items-center gap-2"
            disabled={loading}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>

          <button
            type="button"
            onClick={handleResend}
            disabled={!canResend || loading}
            className="text-accent hover:text-accent-deep flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Resend Code
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationModal;


